import type { UsageRange } from '@olytic/foundation/ai-admin';
import { useEffect, useState } from 'react';

import { authenticatedFetch } from '../auth/authClient';
import { fetchAiUsage, usageView, type UsageView } from './aiUsageApi';

export type AiUsageLoad =
  | { state: 'loading' }
  | { state: 'error'; message: string }
  | { state: 'ready'; view: UsageView };

/** GET /api/ai/usage for `range`, again whenever the range changes or `retry` is called. */
export function useAiUsage(range: UsageRange): { load: AiUsageLoad; retry: () => void } {
  const [load, setLoad] = useState<AiUsageLoad>({ state: 'loading' });
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    const controller = new AbortController();
    setLoad({ state: 'loading' });
    fetchAiUsage(authenticatedFetch, range, { signal: controller.signal }).then(
      (body) => setLoad({ state: 'ready', view: usageView(body) }),
      (error: unknown) => {
        if (!controller.signal.aborted) {
          setLoad({ state: 'error', message: error instanceof Error ? error.message : 'Could not load AI usage.' });
        }
      },
    );
    return () => controller.abort();
  }, [range, attempt]);

  return { load, retry: () => setAttempt((n) => n + 1) };
}
